import { auth, signOut } from './Firebase.jsx';
import { persistor, ReduxStore, resetStore } from '../redux-store/ReduxStore.js';
import { abandonReauth, loginUrl, markDeliberateSignOut } from './sessionExpiry.js';

/**
 * Non-React logout helper that can be called from anywhere (e.g. the axios
 * response interceptor when a token refresh fails). Does not touch React
 * state — the onIdTokenChanged listener in AuthContext.jsx will fire with
 * null after signOut and update the user state on its own.
 */
export async function performSessionCleanup() {
    // A sign-out on purpose: AuthContext must not read the Firebase sign-out below as an expired session.
    markDeliberateSignOut();
    abandonReauth();
    try {
        // Reset the live store too — purge()/sessionStorage.clear() below only drop
        // the persisted copy, so a same-tab re-login could otherwise inherit it.
        ReduxStore.dispatch(resetStore());
    } catch (err) {
        console.error('performSessionCleanup: store reset failed', err);
    }
    try {
        await persistor.purge();
    } catch (err) {
        console.error('performSessionCleanup: persistor.purge failed', err);
    }
    try {
        sessionStorage.clear();
    } catch (err) {
        console.error('performSessionCleanup: sessionStorage.clear failed', err);
    }
    try {
        // DataGrid view-state is no longer persisted, but older sessions may have left
        // `gp_datagrid_*` entries in localStorage. Purge them so one user's saved filters
        // can never carry into the next account signed in on this browser.
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith('gp_datagrid_')) localStorage.removeItem(key);
        }
    } catch (err) {
        console.error('performSessionCleanup: localStorage cleanup failed', err);
    }
    try {
        await signOut(auth);
    } catch (err) {
        console.error('performSessionCleanup: signOut failed', err);
    }
}

/**
 * Send the browser to the sign-in page, with a link back to where the user was (`returnTo`), unless we're already
 * there. replace() keeps the back button from returning to the broken authenticated state; the pathname guard
 * prevents redirect loops when several requests end the session at once.
 */
export function safeRedirectToLogin(returnTo) {
    if (typeof window === 'undefined') return;
    if (window.location.pathname.startsWith('/account')) return;
    window.location.replace(loginUrl(returnTo));
}
